/* eslint-disable indent */
/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

/**
 * Class names used by the validation UI. Centralized so the consolidated
 * styling helper can target the right classes in one place.
 *
 * @readonly
 */
const VALIDATION_CLASSES = Object.freeze({
	DANGER: 'is-danger',
	SUCCESS: 'is-success',
	VALIDATION_TEXT: 'validation-text',
	HELP_DANGER_TEXT: 'has-text-danger',
});

/** Delay (ms) before fading the success state on always-active validation. */
const SUCCESS_FADE_DELAY_MS = 2500;

/**
 * CustomInputHandler — drives the UI side of input validation for any
 * dynamic input whose template wires this provider in via PictForm.
 *
 * Responsibilities:
 *   - Resolve which validation rules apply for an input from the manifest
 *     config (PictForm) and the live DOM element, and keep the element's
 *     HTML5 validation attributes (`required`, `aria-required`) in sync.
 *   - Apply / remove the success / danger styling on the control.
 *   - Inject a human-readable validation message into the help-text wrapper.
 *
 * Visibility gating:
 *   Hidden inputs (on inactive tabs, conditionally hidden by other selections,
 *   etc.) do NOT enforce validation. They get their `required` attribute
 *   cleared at runtime so the browser/framework's `checkValidity()` returns
 *   true, and any stale danger styling / validation-text is scrubbed off.
 *   "Hidden" here means CSS display:none / visibility:hidden on the element
 *   or any ancestor. Inputs scrolled below the viewport remain enforced.
 */
class CustomInputHandler extends PictSectionInputExtension
{
	/**
	 * @param {import('fable')} pFable
	 * @param {Record<string, any>} [pOptions]
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Resolve the live HTMLInputElement for a given selector, or null if absent.
	 *
	 * @param {string} pHTMLSelector
	 * @return {HTMLInputElement|null}
	 */
	resolveInputElement(pHTMLSelector)
	{
		const tmpInputList = this.pict.ContentAssignment.getElement(pHTMLSelector);
		return (tmpInputList && tmpInputList.length > 0) ? tmpInputList[0] : null;
	}

	/**
	 * Resolve the "real" styling target for validation UI.
	 *
	 * Pict-section-form passes the *canonical* (data-bearing) input
	 * selector to our lifecycle hooks — e.g. for Station/DateTime/Date/Time
	 * that's the hidden input that holds the form value, not the visible
	 * input the user actually types into. Native `checkValidity()` on a
	 * `type="hidden"` (or otherwise CSS-hidden) input is barred and
	 * returns true even when empty, so styling against the canonical
	 * silently no-ops.
	 *
	 * This resolver returns the canonical when it's already the visible
	 * required input; otherwise it walks up to the nearest scope
	 * (`.control.control-status` → `.field` → `.column` → parent) and
	 * returns the first visible `[required]` sibling there. Signature-style
	 * inputs (hidden-only, data-validation-surface) fall back to the
	 * canonical since there is no visible required sibling to find.
	 *
	 * @param {string} pHTMLSelector
	 * @return {HTMLInputElement|null}
	 */
	resolveStylingTargetInput(pHTMLSelector)
	{
		const tmpCanonical = this.resolveInputElement(pHTMLSelector);
		if (!tmpCanonical)
		{
			return null;
		}
		// If the canonical isn't a hidden data-bearer, use it directly.
		if (!this._isHiddenDataBearer(tmpCanonical))
		{
			return tmpCanonical;
		}
		// Hidden data-bearer: walk outward looking for a visible required
		// sibling. The scope priority (control → field → column → parent)
		// matches the typical InputType template layering — most data
		// bearers live next to their visible sibling inside the same
		// control wrapper, but Station-style templates keep the hidden
		// bearer at the column level.
		if (typeof tmpCanonical.closest !== 'function')
		{
			return tmpCanonical;
		}
		const tmpScopes = [
			tmpCanonical.closest('.control.control-status'),
			tmpCanonical.closest('.field'),
			tmpCanonical.closest('.column'),
			tmpCanonical.parentElement,
		];
		const tmpSelector = 'input[required]:not([type="hidden"]):not(.is-hidden), select[required], textarea[required]';
		for (let i = 0; i < tmpScopes.length; i++)
		{
			const tmpScope = tmpScopes[i];
			if (!tmpScope || typeof tmpScope.querySelector !== 'function')
			{
				continue;
			}
			const tmpVisible = /** @type {HTMLInputElement|null} */ (tmpScope.querySelector(tmpSelector));
			if (tmpVisible && tmpVisible !== tmpCanonical)
			{
				return tmpVisible;
			}
		}
		return tmpCanonical;
	}

	/**
	 * True when the element is currently rendered to the user (i.e. not
	 * `display:none` / `visibility:hidden` on itself or any ancestor, and
	 * still attached to the DOM). Elements below the viewport count as visible.
	 *
	 * Note: this walks ancestors via getComputedStyle, which is the most
	 * robust check across tab containers, conditionally-rendered wrappers,
	 * and Bulma `is-hidden` style toggles.
	 *
	 * @param {Element|null|undefined} pElement
	 * @return {boolean}
	 */
	isInputVisibleToUser(pElement)
	{
		if (!pElement || pElement.nodeType !== 1)
		{
			return false;
		}
		if (!pElement.isConnected)
		{
			return false;
		}
		// Validation-surface indirection: an input may declare an alternate
		// user-visible element that represents it on screen (e.g. Signature
		// puts `required` on a CSS-hidden <input> while the actual visible
		// surface is a sibling <img>/<button>). When `data-validation-surface`
		// is set, check that element's visibility instead.
		const tmpSurfaceID = (pElement.getAttribute && pElement.getAttribute('data-validation-surface')) || null;
		if (tmpSurfaceID)
		{
			const tmpSurface = document.getElementById(tmpSurfaceID);
			if (tmpSurface)
			{
				return this._walkAncestorVisibility(tmpSurface);
			}
		}
		// Select2 indirection: when Select2 takes over a native <select>, it
		// hides the underlying element (aria-hidden="true", positioned
		// off-screen, class="select2-hidden-accessible") and renders its own
		// visible widget as a `.select2-container` sibling. The native select
		// is logically present and "visible to the user" — just via the
		// container surface. Without this, Pass 3 of applyFormSubmissionStyling
		// short-circuits on every Select2-managed required <select> and
		// never flags empty ones as invalid.
		if (pElement.tagName === 'SELECT' && pElement.classList && pElement.classList.contains('select2-hidden-accessible'))
		{
			const tmpContainer = this._findSelect2ContainerFor(/** @type {HTMLSelectElement} */ (pElement));
			if (tmpContainer)
			{
				return this._walkAncestorVisibility(tmpContainer);
			}
		}
		return this._walkAncestorVisibility(pElement);
	}

	/**
	 * Locate the `.select2-container` widget that Select2 injected for a
	 * given native `<select>`. Select2 normally places it as the immediate
	 * next sibling, but defensive code walks both the sibling chain and the
	 * parent's children to handle layouts that wrap the container.
	 *
	 * @param {HTMLSelectElement} pSelect
	 * @return {Element|null}
	 * @private
	 */
	_findSelect2ContainerFor(pSelect)
	{
		if (!pSelect || pSelect.nodeType !== 1)
		{
			return null;
		}
		let tmpSibling = pSelect.nextElementSibling;
		while (tmpSibling)
		{
			if (tmpSibling.classList && tmpSibling.classList.contains('select2-container'))
			{
				return tmpSibling;
			}
			tmpSibling = tmpSibling.nextElementSibling;
		}
		if (pSelect.parentElement && typeof pSelect.parentElement.querySelector === 'function')
		{
			return pSelect.parentElement.querySelector('.select2-container');
		}
		return null;
	}

	/**
	 * Internal: walk the ancestor chain checking for display:none,
	 * visibility:hidden/collapse, and aria-hidden="true". Returns false if
	 * any ancestor (or the element itself) is hidden by any of those.
	 *
	 * @param {Element} pElement
	 * @return {boolean}
	 * @private
	 */
	_walkAncestorVisibility(pElement)
	{
		let tmpCurrent = /** @type {Element|null} */ (pElement);
		while (tmpCurrent && tmpCurrent.nodeType === 1)
		{
			const tmpStyle = (typeof window !== 'undefined' && window.getComputedStyle) ? window.getComputedStyle(tmpCurrent) : null;
			if (tmpStyle)
			{
				if (tmpStyle.display === 'none')
				{
					return false;
				}
				if (tmpStyle.visibility === 'hidden' || tmpStyle.visibility === 'collapse')
				{
					return false;
				}
			}
			if (tmpCurrent.getAttribute && tmpCurrent.getAttribute('aria-hidden') === 'true')
			{
				return false;
			}
			if (tmpCurrent === document.body)
			{
				break;
			}
			tmpCurrent = tmpCurrent.parentElement;
		}
		return true;
	}

	/**
	 * Sync `required` and `aria-required` on a live DOM element so that the
	 * browser's native validity check (and any framework that consults it)
	 * matches what the user can actually see and interact with.
	 *
	 * @param {HTMLInputElement|null} pElement
	 * @param {boolean} pShouldBeRequired
	 * @return {void}
	 */
	syncRequiredAttributes(pElement, pShouldBeRequired)
	{
		if (!pElement)
		{
			return;
		}
		if (pShouldBeRequired)
		{
			if (!pElement.required)
			{
				pElement.required = true;
			}
			if (pElement.getAttribute('aria-required') !== 'true')
			{
				pElement.setAttribute('aria-required', 'true');
			}
			// Stamp the persistent marker so the pre-submit sweep can find
			// this field even after a future visibility-driven strip flips
			// `required` and `aria-required` back off.
			if (pElement.getAttribute('data-required') !== 'true')
			{
				pElement.setAttribute('data-required', 'true');
			}
		}
		else
		{
			if (pElement.required)
			{
				pElement.required = false;
			}
			if (pElement.hasAttribute('aria-required'))
			{
				pElement.removeAttribute('aria-required');
			}
			// data-required is intentionally NOT removed here — it persists
			// across visibility flips. The only place it should be cleared
			// is when the manifest itself no longer marks the field
			// required, which happens in setValidationInputAttributes when
			// PictForm explicitly says Required: false.
		}
	}

	/**
	 * Walk every "manifest says this is required" input within a form
	 * element and resync its live HTML5 / ARIA required attributes against
	 * current visibility. Strips `required` / `aria-required` /
	 * `aria-invalid` from hidden fields so the browser's checkValidity()
	 * (or any `:invalid` selector consumer) doesn't flag them, and
	 * restores `required` / `aria-required` on visible fields that may
	 * have had them stripped during an earlier hidden state.
	 *
	 * Call this immediately before `formElement.checkValidity()` or a
	 * `:invalid` query in any form-submission flow.
	 *
	 * @param {HTMLElement|null} pFormElement
	 * @return {void}
	 */
	preSubmitVisibilitySweep(pFormElement)
	{
		if (!pFormElement || typeof pFormElement.querySelectorAll !== 'function')
		{
			return;
		}
		// We query data-required (the persistent manifest marker) instead
		// of `[required]` because hidden fields may have already had their
		// `required` attribute stripped — we still need to find them to
		// decide whether to restore.
		const tmpManagedInputs = pFormElement.querySelectorAll('[data-required="true"]');
		for (let i = 0; i < tmpManagedInputs.length; i++)
		{
			const tmpInput = /** @type {HTMLInputElement} */ (tmpManagedInputs[i]);
			const tmpIsVisible = this.isInputVisibleToUser(tmpInput);
			this.syncRequiredAttributes(tmpInput, tmpIsVisible);
			if (!tmpIsVisible)
			{
				// A previously-shown error on a now-hidden field is stale —
				// clear aria-invalid + any leftover is-danger styling so it
				// doesn't lie to AT or paint a tab the user can't see.
				this.syncAriaInvalidAttribute(tmpInput, false);
				const tmpControl = this.resolveControlElement(tmpInput);
				this.clearValidationStyling(tmpControl);
				this.clearValidationHelpText(tmpControl);
			}
		}
	}

	/**
	 * Apply validation styling across an entire form after a submission
	 * attempt. Centralizes everything the validation UI needs to do:
	 *
	 *   - Walk `:invalid` inputs and stamp `is-danger` on the closest
	 *     `.control.control-status` wrapper (uses closest() so flatpickr or
	 *     other providers that wrap the input still find the right
	 *     ancestor).
	 *   - Walk `:valid` inputs and clear validity classes from the same
	 *     wrapper.
	 *   - Mirror the class onto any `.select2-container` sibling within the
	 *     wrapper — Bulma's `.is-danger` styling targets the native
	 *     `.input` / `.select` and doesn't reach Select2's replacement widget.
	 *   - Value-check any `[data-required="true"]` inputs that are CSS-
	 *     hidden or `type="hidden"` (e.g. Signature, which puts `required`
	 *     on a data-bearing input behind an `<img>` surface). Native
	 *     `:invalid` doesn't apply to these so we have to inspect `.value`
	 *     directly.
	 *   - Keep `aria-invalid` in sync on all affected inputs.
	 *
	 * Call this *after* `preSubmitVisibilitySweep()` (which has already
	 * stripped `required` from hidden fields so they don't get flagged).
	 *
	 * Returns true when every visible field passes validation (native +
	 * data-required hidden-bearer checks), so callers can use the return
	 * value as the gate decision and don't need to re-scan the form.
	 *
	 * @param {HTMLElement|null} pFormElement
	 * @return {boolean} true if form passes all visible validations.
	 */
	applyFormSubmissionStyling(pFormElement)
	{
		if (!pFormElement || typeof pFormElement.querySelectorAll !== 'function')
		{
			return true;
		}

		// Wire up the form-level live revalidation listener so subsequent
		// user edits to any input promote a previously-red wrapper to green
		// (or back to red if the user clears a required field) without
		// waiting for another submit. Idempotent — only attaches once.
		this.attachLiveRevalidation(pFormElement);

		let tmpFormIsValid = true;

		const tmpLengthLimited = pFormElement.querySelectorAll('[minlength], [maxlength]');
		for (let i = 0; i < tmpLengthLimited.length; i++)
		{
			const tmpInput = tmpLengthLimited[i];
			if (this.isInputVisibleToUser(tmpInput))
			{
				this.syncLengthValidity(tmpInput);
			}
			else if (typeof (/** @type {HTMLInputElement} */ (tmpInput)).setCustomValidity === 'function')
			{
				(/** @type {HTMLInputElement} */ (tmpInput)).setCustomValidity('');
			}
		}

		// Pass 1: anything failing native HTML5 validation. Catches required
		// fields that are empty, pattern/email/min/max mismatches, etc.
		const tmpInvalidElements = pFormElement.querySelectorAll(':invalid');
		for (let i = 0; i < tmpInvalidElements.length; i++)
		{
			this._applyValidityToWrapperAndKin(/** @type {HTMLElement} */ (tmpInvalidElements[i]), false);
			tmpFormIsValid = false;
		}

		// Pass 2: clear styling from anything that's now valid. Has to run
		// unconditionally so previously-flagged fields go back to green.
		const tmpValidElements = pFormElement.querySelectorAll(':valid');
		for (let i = 0; i < tmpValidElements.length; i++)
		{
			this._applyValidityToWrapperAndKin(/** @type {HTMLElement} */ (tmpValidElements[i]), true);
		}

		// Pass 3: walk every manifest-required input authoritatively. This
		// is the most reliable layer of the three because it doesn't rely
		// on `:valid` / `:invalid` — which silently skip
		//   - `type="hidden"` (always barred)
		//   - `readonly` inputs (flatpickr makes its bound input readonly
		//     by default, so Date/Time/DateTime never match either pseudo)
		//   - inputs with `disabled` (also barred)
		// We inspect `.value` (or `.checked` for checkboxes) directly and
		// apply styling to the right wrapper. Runs last so it overrides
		// any (mis)styling from Pass 1/2 for these fields.
		const tmpDataRequired = pFormElement.querySelectorAll('[data-required="true"]');
		for (let i = 0; i < tmpDataRequired.length; i++)
		{
			const tmpInput = /** @type {HTMLInputElement} */ (tmpDataRequired[i]);
			const tmpIsHiddenBearer = this._isHiddenDataBearer(tmpInput);

			// Visibility gate. For hidden bearers, use the surface/wrapper
			// indirection (the input itself is always CSS-hidden). For
			// visible inputs, check the input directly.
			let tmpIsVisible;
			if (tmpIsHiddenBearer)
			{
				const tmpVisibilityTarget = this._resolveVisibilityTargetForHiddenBearer(tmpInput);
				tmpIsVisible = tmpVisibilityTarget ? this._walkAncestorVisibility(tmpVisibilityTarget) : true;
			}
			else
			{
				tmpIsVisible = this.isInputVisibleToUser(tmpInput);
			}

			if (!tmpIsVisible)
			{
				// Field is on an inactive tab / conditionally hidden — scrub
				// any leftover styling, don't block submit, don't flag.
				const tmpControl = this.resolveControlElement(tmpInput);
				this.clearValidationStyling(tmpControl);
				this.clearValidationHelpText(tmpControl);
				this.syncAriaInvalidAttribute(tmpInput, false);
				continue;
			}

			// Determine "logical validity" — for checkboxes, "checked";
			// otherwise a non-empty, non-sentinel value.
			const tmpHasValue = this._hasRequiredValue(tmpInput);
			// Pass 3 is the authoritative required-field walk. Unlike
			// Pass 1/2 (which walk every :valid element including optional
			// fields), this pass only touches inputs the manifest marked as
			// required — so a non-empty value here unambiguously means
			// "requirement satisfied" and should paint green regardless of
			// prior state. Override the default "only mark success if was
			// danger" gate.
			this._applyValidityToWrapperAndKin(tmpInput, tmpHasValue, { onlyMarkSuccessIfWasDanger: false });
			if (!tmpHasValue)
			{
				tmpFormIsValid = false;
			}
		}

		return tmpFormIsValid;
	}

	/**
	 * Re-apply validity styling for a single input element using DOM state
	 * alone. Used by the form-level live revalidation listener so that the
	 * moment a user corrects a previously-flagged field, the wrapper
	 * promotes from `is-danger` back to `is-success` — without waiting for
	 * another Submit click. The "you fixed it" semantics also mean that
	 * clearing a required field that had passed before flips back to
	 * `is-danger` immediately.
	 *
	 * Validity is read from the DOM:
	 *   - Checkbox + required → `checked`
	 *   - Manifest-required (`data-required="true"`) with no native `required`
	 *     attribute on the DOM element (Select2-managed `<select>`), or a
	 *     hidden data-bearer (Signature, Picker) → a non-empty `.value` that
	 *     isn't one of its `data-empty-values`
	 *   - Anything else → `element.checkValidity()`
	 *
	 * Inputs that are not currently visible to the user (inactive tab,
	 * conditionally hidden) have their styling scrubbed and don't get
	 * flagged — the styling shouldn't lie about a field the user can't
	 * see and can't act on.
	 *
	 * @param {Element|null} pElement
	 * @return {void}
	 */
	revalidateInputElement(pElement)
	{
		if (!pElement || pElement.nodeType !== 1)
		{
			return;
		}
		if (!this.pict || !this.pict.AppData || !this.pict.AppData.Interactive)
		{
			return;
		}

		// Translate hidden data-bearer inputs (e.g. Station's hidden bearer,
		// Signature's <input type="hidden">) to their visible required
		// sibling so styling lands on the user-facing field.
		const tmpResolved = (pElement.id && typeof this.resolveStylingTargetInput === 'function') ?
			this.resolveStylingTargetInput('#' + pElement.id) :
			null;
		const tmpTarget = tmpResolved || /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} */ (pElement);
		if (!tmpTarget || !tmpTarget.tagName)
		{
			return;
		}

		if (!this.isInputVisibleToUser(tmpTarget))
		{
			const tmpControl = this.resolveControlElement(tmpTarget);
			this.clearValidationStyling(tmpControl);
			this.clearValidationHelpText(tmpControl);
			this.syncAriaInvalidAttribute(/** @type {HTMLInputElement} */ (tmpTarget), false);
			return;
		}

		const tmpHasNativeRequired = tmpTarget.hasAttribute && tmpTarget.hasAttribute('required');
		const tmpHasDataRequired = tmpTarget.getAttribute && tmpTarget.getAttribute('data-required') === 'true';
		let tmpIsValid;
		if (tmpTarget.type === 'checkbox' && tmpTarget.required)
		{
			tmpIsValid = !!tmpTarget.checked;
		}
		else if (tmpHasDataRequired && (!tmpHasNativeRequired || this._isHiddenDataBearer(/** @type {HTMLInputElement} */ (tmpTarget))))
		{
			// Manifest-required but native validity can't judge it: the DOM
			// doesn't carry `required` (Select2-managed <select>), or it is a
			// hidden data-bearer, which constraint validation skips. Inspect
			// the value directly — that's the authoritative signal.
			tmpIsValid = this._hasRequiredValue(/** @type {HTMLInputElement} */ (tmpTarget));
		}
		else if (typeof tmpTarget.checkValidity === 'function')
		{
			this.syncLengthValidity(tmpTarget);
			tmpIsValid = tmpTarget.checkValidity();
		}
		else
		{
			tmpIsValid = true;
		}

		this.syncAriaInvalidAttribute(/** @type {HTMLInputElement} */ (tmpTarget), !tmpIsValid);
		// Use the default `onlyMarkSuccessIfWasDanger: true` semantics —
		// only promote to green when the wrapper had previously been red
		// ("you fixed it"). Pass 3 of the bulk submit uses unconditional
		// promotion via an explicit option, but for live edits we don't
		// want every keystroke on an unrelated valid field to suddenly
		// paint green out of nowhere.
		this._applyValidityToWrapperAndKin(/** @type {HTMLElement} */ (tmpTarget), tmpIsValid);

		// Mirror to addon sibling (e.g. unit pill on numeric inputs) so it
		// tracks the same color as the wrapper.
		const tmpControl = this.resolveControlElement(tmpTarget);
		if (tmpControl)
		{
			const tmpAddon = tmpControl.nextElementSibling;
			if (tmpAddon && tmpAddon.classList && tmpAddon.classList.contains('addon'))
			{
				this.applyValidityClasses(/** @type {HTMLElement} */ (tmpAddon), tmpIsValid, { onlyMarkSuccessIfWasDanger: true });
			}
		}
	}

	/**
	 * Attach a delegated input/change/blur listener at the form level so
	 * every input the user touches gets revalidated against the latest
	 * state. Idempotent — re-attaching on the same form is a no-op. The
	 * handler is gated on `form.show-validation` so we don't paint
	 * validation colors on initial page load; only after the first Submit
	 * attempt (which adds `show-validation`) does live feedback kick in.
	 *
	 * Uses a single delegated listener rather than per-input wiring so any
	 * input type — including those whose templates don't list
	 * InputValidation in `DefaultInputExtensions` — gets covered. Capture
	 * phase so it runs even when child providers stop propagation.
	 *
	 * @param {HTMLElement|null} pFormElement
	 * @return {void}
	 */
	attachLiveRevalidation(pFormElement)
	{
		if (!pFormElement || pFormElement.nodeType !== 1)
		{
			return;
		}
		if (pFormElement.dataset && pFormElement.dataset.inputValidationLiveAttached === 'true')
		{
			return;
		}
		if (pFormElement.dataset)
		{
			pFormElement.dataset.inputValidationLiveAttached = 'true';
		}

		const tmpProvider = this;
		const tmpHandler = function(pEvent)
		{
			if (!pFormElement.classList || !pFormElement.classList.contains('show-validation'))
			{
				return;
			}
			const tmpTarget = pEvent.target;
			if (!tmpTarget || !tmpTarget.tagName)
			{
				return;
			}
			if (tmpTarget.tagName !== 'INPUT' && tmpTarget.tagName !== 'SELECT' && tmpTarget.tagName !== 'TEXTAREA')
			{
				return;
			}
			tmpProvider.revalidateInputElement(tmpTarget);
		};

		// Capture phase so the listener fires even if a provider on a
		// child element calls stopPropagation. `blur` doesn't bubble so we
		// listen for it on capture too (or could use focusout; capture is
		// simpler and works cross-browser).
		pFormElement.addEventListener('input', tmpHandler, true);
		pFormElement.addEventListener('change', tmpHandler, true);
		pFormElement.addEventListener('blur', tmpHandler, true);

		// Also bind via jQuery when available. Select2 dispatches selection
		// changes via `$(select).trigger('change')`, which goes through
		// jQuery's own event bus and does NOT fire native addEventListener
		// listeners. Without this jQuery hook, picking a value in a Select2
		// dropdown wouldn't trigger live revalidation. Pict-Section-Form's
		// other providers similarly trigger via jQuery for some inputs.
		const tmpJQuery = (typeof window !== 'undefined' && (window.$ || window.jQuery)) || null;
		if (tmpJQuery)
		{
			tmpJQuery(pFormElement).on('input.inputValidationLive change.inputValidationLive blur.inputValidationLive', function(pEvent)
			{
				tmpHandler(pEvent);
			});
		}
	}

	/**
	 * For a hidden data-bearer input, return the element whose visibility
	 * actually represents "is this field on screen?" — preferring an
	 * explicit `data-validation-surface` pointer, falling back to the
	 * closest `.control.control-status` wrapper, finally the parent.
	 *
	 * @param {HTMLInputElement} pInput
	 * @return {Element|null}
	 * @private
	 */
	_resolveVisibilityTargetForHiddenBearer(pInput)
	{
		if (!pInput || pInput.nodeType !== 1)
		{
			return null;
		}
		const tmpSurfaceID = pInput.getAttribute && pInput.getAttribute('data-validation-surface');
		if (tmpSurfaceID)
		{
			const tmpSurface = document.getElementById(tmpSurfaceID);
			if (tmpSurface)
			{
				return tmpSurface;
			}
		}
		if (typeof pInput.closest === 'function')
		{
			const tmpControl = pInput.closest('.control.control-status');
			if (tmpControl)
			{
				return tmpControl;
			}
		}
		return pInput.parentElement;
	}

	/**
	 * Internal: apply or clear validity classes on the input's
	 * `.control.control-status` wrapper (via its validation surface for a
	 * hidden data-bearer), mirror onto any Select2 container sibling, and sync
	 * `aria-invalid` on the input itself and any combobox in the wrapper.
	 *
	 * Two semantic modes, controlled by `pOptions.onlyMarkSuccessIfWasDanger`:
	 *
	 *   - `true` (default) — submit-style "you fixed it" semantics. Only
	 *     promotes a wrapper to `is-success` when it had been flagged
	 *     `is-danger`. Used by Pass 1/2 of `applyFormSubmissionStyling`,
	 *     which walks every `:valid` element — including non-required
	 *     fields like an empty optional input that happens to match
	 *     `:valid`. Without this gate the form would get a "generic green
	 *     tick" on every passing field, which the test at "does NOT add
	 *     is-success when the input was not previously danger" guards
	 *     against and which matches the single-input submit-style behavior
	 *     in `handleValidationInputStyling`.
	 *
	 *   - `false` — unconditional promotion. Used by Pass 3 of
	 *     `applyFormSubmissionStyling`, which walks only
	 *     `[data-required="true"]` inputs. For an authoritatively-required
	 *     field, a non-empty value IS success and the user should see green
	 *     ("you've satisfied this requirement"), regardless of whether the
	 *     wrapper had ever been red. This is the form-wide "report card"
	 *     behavior on submit: required fields are explicitly green-or-red.
	 *
	 * @param {HTMLElement} pElement
	 * @param {boolean} pIsValid
	 * @param {object} [pOptions]
	 * @param {boolean} [pOptions.onlyMarkSuccessIfWasDanger] - Defaults to true.
	 * @return {void}
	 * @private
	 */
	_applyValidityToWrapperAndKin(pElement, pIsValid, pOptions)
	{
		const tmpOptions = pOptions || { onlyMarkSuccessIfWasDanger: true };
		const tmpControl = this._findControlStatusWrapper(pElement);
		const tmpTarget = tmpControl || (pElement.classList && pElement.classList.contains('input') ? pElement : null);

		if (tmpTarget)
		{
			this.applyValidityClasses(/** @type {HTMLElement} */ (tmpTarget), pIsValid, tmpOptions);
		}

		// Select2 replaces the native <select> with its own container that
		// Bulma's class-based styling doesn't reach. Mirror the validity
		// class onto every Select2 container inside this control so the
		// user-visible widget reflects the state. Same promotion semantics
		// (option flag) apply to the mirror as to the control wrapper.
		if (tmpControl && typeof tmpControl.querySelectorAll === 'function')
		{
			const tmpSelect2Containers = tmpControl.querySelectorAll('.select2-container');
			for (let i = 0; i < tmpSelect2Containers.length; i++)
			{
				this.applyValidityClasses(/** @type {HTMLElement} */ (tmpSelect2Containers[i]), pIsValid, tmpOptions);
			}
			// A custom widget's focusable combobox is what assistive tech reads, not the input behind it.
			const tmpComboboxes = tmpControl.querySelectorAll('[role="combobox"]');
			for (let i = 0; i < tmpComboboxes.length; i++)
			{
				this.syncAriaInvalidAttribute(/** @type {HTMLInputElement} */ (tmpComboboxes[i]), !pIsValid);
			}
		}

		// Sync aria-invalid on the input itself when applicable.
		if (pElement.tagName === 'INPUT' || pElement.tagName === 'SELECT' || pElement.tagName === 'TEXTAREA')
		{
			this.syncAriaInvalidAttribute(/** @type {HTMLInputElement} */ (pElement), !pIsValid);
		}
	}

	/**
	 * True when an input is functionally hidden — either `type="hidden"` or
	 * its computed style is display:none — meaning native constraint
	 * validation (`:invalid`) won't apply to it and we have to inspect its
	 * value directly for required-ness.
	 *
	 * @param {HTMLInputElement} pElement
	 * @return {boolean}
	 * @private
	 */
	_isHiddenDataBearer(pElement)
	{
		if (!pElement || pElement.tagName !== 'INPUT')
		{
			return false;
		}
		if (pElement.type === 'hidden')
		{
			return true;
		}
		// Bulma's is-hidden class sets display:none. Other CSS-driven hides
		// (custom classes, inline style) are covered by the computed style
		// check.
		const tmpStyle = (typeof window !== 'undefined' && window.getComputedStyle) ?
			window.getComputedStyle(pElement) :
			null;
		if (tmpStyle && tmpStyle.display === 'none')
		{
			return true;
		}
		return false;
	}

	/**
	 * Sync `aria-invalid` on a live DOM element. Pass `true` to mark the
	 * input as invalid (announces "invalid entry" via screen readers), or
	 * `false` to clear the attribute entirely. We intentionally remove the
	 * attribute on valid state rather than setting aria-invalid="false" — the
	 * latter announces "valid" on every keystroke for live-validation
	 * inputs, which is noisy. ARIA's default semantic when the attribute is
	 * absent is "not yet validated / no judgment", which is what we want.
	 *
	 * @param {HTMLInputElement|null} pElement
	 * @param {boolean} pIsInvalid - true → mark invalid, false → clear.
	 * @return {void}
	 */
	syncAriaInvalidAttribute(pElement, pIsInvalid)
	{
		if (!pElement)
		{
			return;
		}
		if (pIsInvalid)
		{
			if (pElement.getAttribute('aria-invalid') !== 'true')
			{
				pElement.setAttribute('aria-invalid', 'true');
			}
		}
		else if (pElement.hasAttribute('aria-invalid'))
		{
			pElement.removeAttribute('aria-invalid');
		}
	}

	/**
	 * Programmatically pair an input to its help-text wrapper via
	 * `aria-describedby`. The wrapper holds both the Description text and
	 * any injected validation-text, so screen readers announce both as part
	 * of the input. Idempotent — safe to call repeatedly.
	 *
	 * Looks up the wrapper via the same parent-of-parent traversal we use
	 * for validation-text injection, so it stays consistent with the actual
	 * markup the templates emit.
	 *
	 * @param {HTMLInputElement|null} pInputElement
	 * @return {void}
	 */
	linkInputToHelpText(pInputElement)
	{
		if (!pInputElement || !pInputElement.id)
		{
			return;
		}
		const tmpControl = this.resolveControlElement(pInputElement);
		if (!tmpControl)
		{
			return;
		}
		const tmpHelpWrapper = tmpControl.parentElement && tmpControl.parentElement.parentElement ?
			tmpControl.parentElement.parentElement.querySelector('.help') :
			null;
		if (!tmpHelpWrapper)
		{
			return;
		}
		const tmpHelpID = tmpHelpWrapper.id || ('HELP-FOR-' + pInputElement.id);
		if (!tmpHelpWrapper.id)
		{
			tmpHelpWrapper.id = tmpHelpID;
		}
		const tmpExisting = pInputElement.getAttribute('aria-describedby') || '';
		// Preserve any other tokens an upstream template may have already set.
		if (!tmpExisting.split(/\s+/).includes(tmpHelpID))
		{
			pInputElement.setAttribute('aria-describedby', tmpExisting ? `${tmpExisting} ${tmpHelpID}`.trim() : tmpHelpID);
		}
	}

	/**
	 * Locate the "control" wrapper used for is-danger / is-success styling.
	 * Walks the ancestor chain via closest() so deeply-nested inputs (e.g.
	 * flatpickr-wrapped pickers, Station provider's masked input) still find
	 * the right wrapper rather than only checking the direct parent.
	 * Falls back to the input element itself when it carries the `input`
	 * class but has no .control.control-status ancestor.
	 *
	 * @param {HTMLInputElement} pElement
	 * @return {HTMLElement|null}
	 */
	resolveControlElement(pElement)
	{
		if (!pElement)
		{
			return null;
		}
		const tmpControl = this._findControlStatusWrapper(pElement);
		if (tmpControl)
		{
			return tmpControl;
		}
		if (pElement.classList && pElement.classList.contains('input'))
		{
			return pElement;
		}
		return null;
	}

	/**
	 * The `.control.control-status` wrapper that carries an input's validity classes: its own ancestor,
	 * or — for a hidden value input rendered apart from its visible widget — the one around its
	 * `data-validation-surface`.
	 *
	 * @param {Element} pElement
	 * @return {HTMLElement|null}
	 * @private
	 */
	_findControlStatusWrapper(pElement)
	{
		if (!pElement || typeof pElement.closest !== 'function')
		{
			return null;
		}
		const tmpOwnControl = /** @type {HTMLElement|null} */ (pElement.closest('.control.control-status'));
		if (tmpOwnControl)
		{
			return tmpOwnControl;
		}
		const tmpSurfaceID = pElement.getAttribute && pElement.getAttribute('data-validation-surface');
		const tmpSurface = tmpSurfaceID ? document.getElementById(tmpSurfaceID) : null;
		if (tmpSurface && typeof tmpSurface.closest === 'function')
		{
			return /** @type {HTMLElement|null} */ (tmpSurface.closest('.control.control-status'));
		}
		return null;
	}

	/**
	 * True when a manifest-required input holds a real value: `checked` for a checkbox, otherwise a
	 * non-empty value that is not one of the sentinels the input declares in `data-empty-values` (e.g. a
	 * picker whose unset Number value marshals in as "0").
	 *
	 * @param {HTMLInputElement} pElement
	 * @return {boolean}
	 * @private
	 */
	_hasRequiredValue(pElement)
	{
		if (pElement.type === 'checkbox')
		{
			return pElement.checked;
		}
		const tmpValue = (pElement.value == null) ? '' : String(pElement.value);
		if (tmpValue.length < 1)
		{
			return false;
		}
		return !this._readDeclaredEmptyValues(pElement).includes(tmpValue);
	}

	/**
	 * The values an input declares as "empty" through its `data-empty-values` JSON array, as strings. A
	 * missing or malformed attribute declares none.
	 *
	 * @param {Element} pElement
	 * @return {Array<string>}
	 * @private
	 */
	_readDeclaredEmptyValues(pElement)
	{
		const tmpRaw = pElement.getAttribute ? pElement.getAttribute('data-empty-values') : null;
		if (!tmpRaw)
		{
			return [];
		}
		try
		{
			const tmpParsed = JSON.parse(tmpRaw);
			return Array.isArray(tmpParsed) ? tmpParsed.map(String) : [];
		}
		catch (pError)
		{
			return [];
		}
	}

	/**
	 * The length limit a text value breaks, judged from the value and the element's `minlength` /
	 * `maxlength` attributes. An empty value breaks neither; `required` covers that.
	 *
	 * @param {Element|null} pElement
	 * @return {'short'|'long'|null}
	 * @private
	 */
	_readLengthViolation(pElement)
	{
		const tmpElement = /** @type {HTMLInputElement|HTMLTextAreaElement|null} */ (pElement);
		if (!tmpElement || typeof tmpElement.getAttribute !== 'function' || typeof tmpElement.value !== 'string' || tmpElement.value.length < 1)
		{
			return null;
		}
		const tmpMinLength = parseInt(tmpElement.getAttribute('minlength'), 10);
		if (!isNaN(tmpMinLength) && tmpElement.value.length < tmpMinLength)
		{
			return 'short';
		}
		const tmpMaxLength = parseInt(tmpElement.getAttribute('maxlength'), 10);
		if (!isNaN(tmpMaxLength) && tmpElement.value.length > tmpMaxLength)
		{
			return 'long';
		}
		return null;
	}

	/**
	 * Carry a broken `minlength` / `maxlength` into the element's custom validity, so `checkValidity()`,
	 * `:invalid` and the Submit gate see it. Browsers apply the two attributes only to text the user typed,
	 * and the form writes each value back into its input after a change, which drops the browser's own check.
	 *
	 * @param {Element|null} pElement
	 * @return {void}
	 */
	syncLengthValidity(pElement)
	{
		const tmpElement = /** @type {HTMLInputElement|HTMLTextAreaElement|null} */ (pElement);
		if (!tmpElement || typeof tmpElement.setCustomValidity !== 'function' || typeof tmpElement.hasAttribute !== 'function')
		{
			return;
		}
		if (!tmpElement.hasAttribute('minlength') && !tmpElement.hasAttribute('maxlength'))
		{
			return;
		}
		const tmpViolation = this._readLengthViolation(tmpElement);
		tmpElement.setCustomValidity(tmpViolation ? this._buildLengthMessage(tmpElement, tmpViolation) : '');
	}

	/**
	 * @param {HTMLInputElement|HTMLTextAreaElement} pElement
	 * @param {'short'|'long'} pViolation
	 * @return {string}
	 * @private
	 */
	_buildLengthMessage(pElement, pViolation)
	{
		if (pViolation === 'short')
		{
			return `Please lengthen this text to at least ${pElement.getAttribute('minlength')} characters`;
		}
		return `Please shorten this text to no more than ${pElement.getAttribute('maxlength')} characters`;
	}

	/**
	 * Resolve the validation rules that apply to a given input, taking both
	 * the manifest config (PictForm) and any HTML attributes already on the
	 * DOM into account, and synchronize the element's `required` /
	 * `aria-required` attributes to reflect visibility.
	 *
	 * Visibility rule: if the element exists but is not visible to the user,
	 * return an empty rules object so callers treat the input as having no
	 * active validation — the field cannot fail a check the user can't see.
	 *
	 * @param {object} pInput
	 * @param {string} pValue
	 * @param {string} pHTMLSelector
	 * @return {Record<string, any>} The validation rules.
	 */
	setValidationInputAttributes(pInput, pValue, pHTMLSelector)
	{
		const tmpRules = {};
		const tmpElement = this.resolveInputElement(pHTMLSelector);
		const tmpPictForm = (pInput && pInput.PictForm) ? pInput.PictForm : null;

		// Determine what the manifest wants for this input. When PictForm is
		// present, treat it as authoritative — "required-ness is determined
		// by the manifest config" per project semantics. Only fall back to
		// inspecting the DOM when no manifest config was passed in at all.
		const tmpWantsRequired = tmpPictForm ?
			!!tmpPictForm.Required :
			!!(tmpElement && tmpElement.required);

		// Visibility short-circuit. We do this BEFORE returning rules so we
		// can also strip the DOM-level `required` attribute, which keeps the
		// browser's checkValidity() honest at submit time.
		if (tmpElement && !this.isInputVisibleToUser(tmpElement))
		{
			this.syncRequiredAttributes(tmpElement, false);
			return tmpRules;
		}

		// Keep the DOM in sync with what the manifest says about this input.
		// This also adds aria-required for accessibility on every required field.
		if (tmpElement)
		{
			this.syncRequiredAttributes(tmpElement, tmpWantsRequired);
			// Manifest is the source of truth for the persistent marker.
			// If the manifest is present AND explicitly says not required,
			// strip data-required so the pre-submit sweep doesn't try to
			// re-mark this field. (Visibility-driven syncs above preserve
			// the marker — only an explicit manifest negative clears it.)
			if (tmpPictForm && !tmpWantsRequired && tmpElement.hasAttribute('data-required'))
			{
				tmpElement.removeAttribute('data-required');
			}
		}

		if (tmpWantsRequired)
		{
			tmpRules.required = true;
		}

		// For each remaining rule, prefer the manifest value when present.
		// Fall back to the DOM value only when the element actually has the
		// attribute set — relying on the IDL property's truthiness is unsafe
		// because browsers (and JSDOM) expose non-spec defaults like 524288
		// for maxLength when the attribute isn't authored.
		const tmpMinNumber = this._pickRuleValue(tmpPictForm, 'MinNumber', tmpElement, 'min', 'min');
		if (tmpMinNumber !== undefined)
		{
		tmpRules.min = tmpMinNumber;
		}

		const tmpMaxNumber = this._pickRuleValue(tmpPictForm, 'MaxNumber', tmpElement, 'max', 'max');
		if (tmpMaxNumber !== undefined)
		{
		tmpRules.max = tmpMaxNumber;
		}

		const tmpStep = this._pickRuleValue(tmpPictForm, 'StepNumber', tmpElement, 'step', 'step');
		if (tmpStep !== undefined)
		{
		tmpRules.step = tmpStep;
		}

		const tmpMinLength = this._pickRuleValue(tmpPictForm, 'MinLength', tmpElement, 'minLength', 'minlength');
		if (tmpMinLength !== undefined)
		{
		tmpRules.minlength = tmpMinLength;
		}

		const tmpMaxLength = this._pickRuleValue(tmpPictForm, 'MaxLength', tmpElement, 'maxLength', 'maxlength');
		if (tmpMaxLength !== undefined)
		{
		tmpRules.maxlength = tmpMaxLength;
		}

		const tmpPattern = this._pickRuleValue(tmpPictForm, 'Pattern', tmpElement, 'pattern', 'pattern');
		if (tmpPattern !== undefined)
		{
		tmpRules.pattern = tmpPattern;
		}

		return tmpRules;
	}

	/**
	 * Pick a rule value, manifest first then DOM. Returns undefined when
	 * neither side has a meaningful value (so callers can omit the key
	 * entirely from the rules object).
	 *
	 * @param {object|null} pPictForm - Manifest config, if any.
	 * @param {string} pManifestKey - Key on PictForm to read.
	 * @param {HTMLInputElement|null} pElement - Live DOM element, if any.
	 * @param {string} pDomKey - IDL property name on the element.
	 * @param {string} pAttributeName - HTML attribute name (used with hasAttribute to detect "actually set").
	 * @return {any}
	 */
	_pickRuleValue(pPictForm, pManifestKey, pElement, pDomKey, pAttributeName)
	{
		if (pPictForm && pPictForm[pManifestKey] !== undefined && pPictForm[pManifestKey] !== null)
		{
			return pPictForm[pManifestKey];
		}
		if (pElement && pElement.hasAttribute && pElement.hasAttribute(pAttributeName))
		{
			return pElement[pDomKey];
		}
		return undefined;
	}

	/**
	 * True if any validation rules apply to this input right now (taking the
	 * visibility gate into account, since setValidationInputAttributes does).
	 *
	 * @param {object} pInput
	 * @param {string} pValue
	 * @param {string} pHTMLSelector
	 * @return {boolean}
	 */
	hasInputValidation(pInput, pValue, pHTMLSelector)
	{
		const tmpRules = this.setValidationInputAttributes(pInput, pValue, pHTMLSelector);
		return Object.keys(tmpRules).length > 0;
	}

	/**
	 * Apply / remove validity classes on a control wrapper. Shared by both
	 * the submit-style and change-style validation flows so the class-swap
	 * logic lives in one place.
	 *
	 * @param {HTMLElement|null} pControl - The control wrapper or input itself.
	 * @param {boolean} pIsValid - Whether the input is currently valid.
	 * @param {object} [pOptions]
	 * @param {boolean} [pOptions.clearBoth] - Strip both classes regardless of validity (e.g. empty input on change).
	 * @param {boolean} [pOptions.fadeSuccess] - Remove the success class after SUCCESS_FADE_DELAY_MS.
	 * @param {boolean} [pOptions.onlyMarkSuccessIfWasDanger] - Submit-style behavior: only add is-success when the field had been marked is-danger.
	 * @return {void}
	 */
	applyValidityClasses(pControl, pIsValid, pOptions)
	{
		if (!pControl)
		{
			return;
		}
		const tmpOptions = pOptions || {};
		if (tmpOptions.clearBoth)
		{
			pControl.classList.remove(VALIDATION_CLASSES.SUCCESS);
			pControl.classList.remove(VALIDATION_CLASSES.DANGER);
			return;
		}
		if (pIsValid)
		{
			if (tmpOptions.onlyMarkSuccessIfWasDanger)
			{
				if (pControl.classList.contains(VALIDATION_CLASSES.DANGER))
				{
					pControl.classList.add(VALIDATION_CLASSES.SUCCESS);
				}
			}
			else
			{
				pControl.classList.add(VALIDATION_CLASSES.SUCCESS);
			}
			pControl.classList.remove(VALIDATION_CLASSES.DANGER);
		}
		else
		{
			pControl.classList.remove(VALIDATION_CLASSES.SUCCESS);
			pControl.classList.add(VALIDATION_CLASSES.DANGER);
		}
		if (tmpOptions.fadeSuccess && pControl.classList.contains(VALIDATION_CLASSES.SUCCESS))
		{
			setTimeout(() =>
			{
				pControl.classList.remove(VALIDATION_CLASSES.SUCCESS);
			}, SUCCESS_FADE_DELAY_MS);
		}
	}

	/**
	 * Clear all validation styling on a control wrapper plus any addon sibling.
	 * Used when an input transitions from visible→hidden so it doesn't leave
	 * a stale red highlight behind on a tab the user can no longer see.
	 *
	 * @param {HTMLElement|null} pControl
	 * @return {void}
	 */
	clearValidationStyling(pControl)
	{
		if (!pControl)
		{
			return;
		}
		this.applyValidityClasses(pControl, true, { clearBoth: true });
		const tmpAddon = pControl.nextElementSibling;
		if (tmpAddon && tmpAddon.classList.contains('addon'))
		{
			this.applyValidityClasses(/** @type {HTMLElement} */ (tmpAddon), true, { clearBoth: true });
		}
	}

	/**
	 * Strip the validation-text node from the help wrapper, if present.
	 *
	 * @param {HTMLElement|null} pControl
	 * @return {void}
	 */
	clearValidationHelpText(pControl)
	{
		if (!pControl)
		{
			return;
		}
		// Tier 5: drop any aria-errormessage pointing at a validation-text
		// node we're clearing, so AT never references a dangling id. Scope
		// the sweep to this field so we don't disturb other inputs.
		const tmpErrorScope = (typeof pControl.closest === 'function' && pControl.closest('.field')) || pControl;
		if (tmpErrorScope && typeof tmpErrorScope.querySelectorAll === 'function')
		{
			const tmpReferers = tmpErrorScope.querySelectorAll('[aria-errormessage]');
			for (let i = 0; i < tmpReferers.length; i++)
			{
				tmpReferers[i].removeAttribute('aria-errormessage');
			}
		}
		const tmpHelpWrapper = pControl.parentElement && pControl.parentElement.parentElement ?
			pControl.parentElement.parentElement.querySelector('.help') :
			null;
		if (!tmpHelpWrapper)
		{
			return;
		}
		const tmpExisting = tmpHelpWrapper.querySelector('.' + VALIDATION_CLASSES.VALIDATION_TEXT);
		if (tmpExisting)
		{
			tmpExisting.remove();
		}
	}

	/**
	 * Handle the validation class swapping for the input (submit-style flow).
	 * If the input is not visible to the user, this no-ops and proactively
	 * scrubs any stale danger styling so a previously-visible field that
	 * got hidden doesn't leave error UI on a tab the user can't see.
	 *
	 * @param {object} pInput
	 * @param {string} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} pRowIndex
	 * @return {void}
	 */
	handleValidationInputStyling(pInput, pValue, pHTMLSelector, pRowIndex)
	{
		if (!this.pict.AppData.Interactive)
		{
			// if we're not in interactive mode, we don't want to show the validation UI
			return;
		}
		// Use the styling-target resolver so hidden data-bearer inputs
		// (Station, DateTime, Date, Time) are translated to their visible
		// required sibling. Without this, the canonical selector points at
		// a type=hidden input that's barred from native validation, and
		// styling silently no-ops on every keystroke.
		const tmpInput = this.resolveStylingTargetInput(pHTMLSelector);
		if (!tmpInput)
		{
			return;
		}
		const tmpControl = this.resolveControlElement(tmpInput);

		if (!this.isInputVisibleToUser(tmpInput))
		{
			// Field is not currently visible to the user — clean up any prior
			// styling and bail without applying new danger state.
			this.clearValidationStyling(tmpControl);
			this.clearValidationHelpText(tmpControl);
			this.syncAriaInvalidAttribute(tmpInput, false);
			return;
		}

		this.syncLengthValidity(tmpInput);
		// NOTE: For checkboxes, we only check validity if the field is required, otherwise an unchecked checkbox will always be invalid but that's not the desired behavior
		const tmpIsValid = (tmpInput.type === 'checkbox' && tmpInput.required) ? tmpInput.checked : tmpInput.checkValidity();

		this.syncAriaInvalidAttribute(tmpInput, !tmpIsValid);

		// if we can't find the control element, there's not much else we can do
		if (!tmpControl)
		{
			this.pict.log.warn('Could not find the control element for input validation styling.');
			return;
		}

		this.applyValidityClasses(tmpControl, tmpIsValid, { onlyMarkSuccessIfWasDanger: true });
		const tmpAddon = tmpControl.nextElementSibling;
		if (tmpAddon && tmpAddon.classList.contains('addon'))
		{
			this.applyValidityClasses(/** @type {HTMLElement} */ (tmpAddon), tmpIsValid, { onlyMarkSuccessIfWasDanger: true });
		}
	}

	/**
	 * Build the validation message text string for the input - could be more
	 * than one error, so we join them.
	 *
	 * @param {HTMLInputElement} pInputElement
	 * @return {string}
	 */
	buildValidationMessageTextString(pInputElement)
	{
		const tmpMessageParts = [];
		if (pInputElement.validity.valid)
		{
			return '';
		}
		if (pInputElement.validity.valueMissing)
		{
			tmpMessageParts.push('This field is required');
		}
		if (pInputElement.validity.typeMismatch)
		{
			tmpMessageParts.push('Please enter a value of the correct type');
		}
		if (pInputElement.validity.patternMismatch)
		{
			tmpMessageParts.push(`Please match the requested format <format>${pInputElement.pattern}</format>`);
		}
		const tmpLengthViolation = this._readLengthViolation(pInputElement);
		if (pInputElement.validity.tooLong || tmpLengthViolation === 'long')
		{
			tmpMessageParts.push(this._buildLengthMessage(pInputElement, 'long'));
		}
		if (pInputElement.validity.tooShort || tmpLengthViolation === 'short')
		{
			tmpMessageParts.push(this._buildLengthMessage(pInputElement, 'short'));
		}
		if (pInputElement.min && pInputElement.max && (pInputElement.validity.rangeUnderflow || pInputElement.validity.rangeOverflow))
		{
			tmpMessageParts.push(`Please enter a value between ${pInputElement.min} and ${pInputElement.max}`);
		}
		else if (pInputElement.validity.rangeUnderflow)
		{
			tmpMessageParts.push(`Please increase the value to at least ${pInputElement.min}`);
		}
		else if (pInputElement.validity.rangeOverflow)
		{
			tmpMessageParts.push(`Please decrease the value to less than or equal to ${pInputElement.max}`);
		}
		if (pInputElement.validity.stepMismatch)
		{
			if (pInputElement.step == '1')
			{
				tmpMessageParts.push('Please enter a valid whole number.');
			}
			else
			{
				tmpMessageParts.push('Please enter a valid value with the correct decimal precision. The allowed step value is ' + pInputElement.step);
			}
		}
		return tmpMessageParts.length ? tmpMessageParts.join('; ') : '';
	}

	/**
	 * Handle the validation UI for the input on a per-change basis (live
	 * feedback as the user types/picks). Differs from
	 * `handleValidationInputStyling` in that it trusts the caller's
	 * `pValidResult` rather than calling `checkValidity()`, and fades the
	 * success class after a short delay.
	 *
	 * @param {object} pInput
	 * @param {string} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} [pRowIndex]
	 * @param {boolean} [pValidResult]
	 * @return {void}
	 */
	handleAlwaysActiveInputValidation(pInput, pValue, pHTMLSelector, pRowIndex, pValidResult = false)
	{
		if (!this.pict.AppData.Interactive)
		{
			// if we're not in interactive mode, we don't want to show the validation UI
			return;
		}
		const tmpInput = this.resolveStylingTargetInput(pHTMLSelector);
		if (!tmpInput)
		{
			return;
		}
		const tmpControl = this.resolveControlElement(tmpInput) || tmpInput;

		if (!this.isInputVisibleToUser(tmpInput))
		{
			this.clearValidationStyling(tmpControl);
			this.syncAriaInvalidAttribute(tmpInput, false);
			return;
		}

		if (!pValue)
		{
			// Empty value means no judgment yet — clear both class state and
			// aria-invalid so screen readers don't carry over a stale verdict.
			this.applyValidityClasses(tmpControl, true, { clearBoth: true });
			this.syncAriaInvalidAttribute(tmpInput, false);
			return;
		}
		this.syncAriaInvalidAttribute(tmpInput, !pValidResult);
		this.applyValidityClasses(tmpControl, !!pValidResult, { fadeSuccess: true });
	}

	/**
	 * Handle the injection of validation help text into the input's help
	 * text wrapper. No-ops + scrubs prior help text when the input is hidden.
	 *
	 * @param {object} pInput
	 * @param {string} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} pRowIndex
	 * @return {void}
	 */
	handleValidationHelpTextElementUpdate(pInput, pValue, pHTMLSelector, pRowIndex)
	{
		if (!this.pict.AppData.Interactive)
		{
			// if we're not in interactive mode, we don't want to show the validation UI
			return;
		}
		const tmpInput = this.resolveStylingTargetInput(pHTMLSelector);
		if (!tmpInput)
		{
			return;
		}
		const tmpControl = this.resolveControlElement(tmpInput);

		if (!this.isInputVisibleToUser(tmpInput))
		{
			this.clearValidationHelpText(tmpControl);
			return;
		}

		// if we can't find the control element, there's not much else we can do
		if (!tmpControl)
		{
			this.pict.log.warn('Could not find the control element for input validation help text injection.');
			return;
		}
		const tmpHelpWrapper = tmpControl.parentElement && tmpControl.parentElement.parentElement ?
			tmpControl.parentElement.parentElement.querySelector('.help') :
			null;
		if (!tmpHelpWrapper)
		{
			return;
		}

		// Remove any previous validation-text node before injecting a fresh one.
		const tmpExisting = tmpHelpWrapper.querySelector('.' + VALIDATION_CLASSES.VALIDATION_TEXT);
		if (tmpExisting)
		{
			tmpExisting.remove();
		}

		const tmpIsValid = tmpInput.type === 'checkbox' ? tmpInput.checked : tmpInput.checkValidity();
		if (tmpIsValid)
		{
			// if the input is valid, we don't need to show any validation text.
			// Drop the aria-errormessage pointer too so AT doesn't reference a
			// node we just removed (Tier 5).
			tmpInput.removeAttribute('aria-errormessage');
			return;
		}

		const tmpValidationTextMessage = this.buildValidationMessageTextString(tmpInput);
		const tmpValidationTextElement = document.createElement('div');
		tmpValidationTextElement.classList.add(VALIDATION_CLASSES.VALIDATION_TEXT);
		tmpValidationTextElement.classList.add(VALIDATION_CLASSES.HELP_DANGER_TEXT);
		// role="alert" makes screen readers announce the message when the
		// node is inserted. assertive live region + atomic so the full
		// message reads as one unit even when parts get rewritten in place.
		tmpValidationTextElement.setAttribute('role', 'alert');
		tmpValidationTextElement.setAttribute('aria-live', 'assertive');
		tmpValidationTextElement.setAttribute('aria-atomic', 'true');
		// Tier 5: point the input at the message node via aria-errormessage.
		// The node id derives from the input's own id (which already encodes
		// the tabular row key, so rows don't collide). AT only honours
		// aria-errormessage when aria-invalid="true" is also present —
		// syncAriaInvalidAttribute manages that in the styling flow.
		if (tmpInput.id)
		{
			const tmpMessageID = 'VALIDATION-MESSAGE-FOR-' + tmpInput.id;
			tmpValidationTextElement.id = tmpMessageID;
			tmpInput.setAttribute('aria-errormessage', tmpMessageID);
		}
		tmpHelpWrapper.insertAdjacentElement('afterbegin', tmpValidationTextElement);
		tmpValidationTextElement.innerHTML = tmpValidationTextMessage || '';
	}

	/**
	 * Fires when an input is first rendered into the DOM. We use it to wire
	 * the input to its help-text wrapper via aria-describedby so screen
	 * readers announce the description (and any later-injected validation
	 * message) as part of the input.
	 *
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pRow
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.linkInputToHelpText(this.resolveInputElement(pHTMLSelector));
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Fires when a tabular input is first rendered. Same aria-describedby
	 * wiring as onInputInitialize, applied per row.
	 *
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} pRowIndex
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.linkInputToHelpText(this.resolveInputElement(pHTMLSelector));
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the change event for the data in the select input.
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (this.hasInputValidation(pInput, pValue, pHTMLSelector))
		{
			this.handleValidationInputStyling(pInput, pValue, pHTMLSelector, 0);
			this.handleValidationHelpTextElementUpdate(pInput, pValue, pHTMLSelector, 0);
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Handles the change event for tabular data.
	 *
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number} pRowIndex
	 * @param {string} pTransactionGUID
	 * @return {any}
	 */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (this.hasInputValidation(pInput, pValue, pHTMLSelector))
		{
			this.handleValidationInputStyling(pInput, pValue, pHTMLSelector, pRowIndex);
			this.handleValidationHelpTextElementUpdate(pInput, pValue, pHTMLSelector, pRowIndex);
		}
		return super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
